import {Container, Stack} from '@mui/material';
import React, {Fragment, useEffect, useState} from 'react';

import {RefundTaggingEnum} from 'main/analytics/gaTagsEnum';
import {ContactInfo, CTAButtonFixed, MainFooter, StyledGrid} from 'presentation/components';
import {Bank} from 'presentation/components/ChangeData/Bank';
import {DiscardChangesModal} from 'presentation/components/ChangeData/DiscardChangesModal';
import {emailRegexValidation, FeaturesEnum} from 'presentation/constants';
import {usePersonalData} from 'presentation/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {useGetBeneficiaryBankDataQuery} from 'presentation/pages/private/profile/queries/useGetBeneficiaryBankDataQuery';
import {useValidateRefundBankQuery} from 'presentation/pages/private/profile/queries/useValidateRefundBankQuery';
import {BankValidityEnum} from 'domain/usecases/private/refund/request/get-validate-bank';
import {EmptyInfoModal, UserInfo} from 'presentation/pages/private/refund/components';
import {RefundVariantEnum} from 'presentation/pages/private/refund/constants/refundVariantEnum';
import {translations} from 'presentation/translations-new';
import {useFormChangeDataStore} from '../../pages/Request/store/useFormChangeDataStore';
import {FormStatesEnum, FormStatesType} from '../../pages/Request/types';
import {BankAlert} from '../BankAlert';
import {EmptyInfoModalEnum} from '../EmptyInfoModal/types';
import {StyledBox, StyledFooterContainer} from './styles';
import {BeneficiaryDataProps, Modals} from './types';

const {keep} = translations['pt-br'].commons;
const {invalidBankAlert} = translations['pt-br'].privateFlow.profile.changeData.bank;

export const BeneficiaryData = ({
	variant = RefundVariantEnum.DEFAULT,
	handleNextStep,
}: BeneficiaryDataProps) => {
	const {currentEmail, currentPhone} = usePersonalData();
	const formChangeData = useFormChangeDataStore(state => state.form);
	const setFormChangeData = useFormChangeDataStore(state => state.setForm);
	const resetFormChangeData = useFormChangeDataStore(state => state.reset);
	const {isTablet, betweenMobileAndTablet} = useMediaQueries();
	const [emptyVariantState, setEmptyVariantState] = useState<EmptyInfoModalEnum>(
		EmptyInfoModalEnum.CONTACT_AND_BANK,
	);
	const [openModals, setOpenModals] = useState<Record<Modals, boolean>>({
		changeInfo: false,
		discardChanges: false,
	});

	const {
		isLoading: loadingBankData,
		isError: errorBankData,
		data: bankData,
	} = useGetBeneficiaryBankDataQuery();

	const {isEnabled: isRefundBankAndAgencyEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_REFUND_BANK_AND_AGENCY,
	);
	const {data: validateBankData, isLoading: loadingValidateBank} = useValidateRefundBankQuery({
		bankCode: bankData?.bank?.code ?? '',
		agencyCode: bankData?.agency ?? '',
		enabled: isRefundBankAndAgencyEnabled,
	});

	const isDefault = variant === RefundVariantEnum.DEFAULT;
	const isAdditionalDocuments = variant === RefundVariantEnum.ADDITIONAL_DOCUMENTS;
	const isPreview = variant === RefundVariantEnum.PREVIEW;
	const currentEmailIsInvalid = !emailRegexValidation.test(currentEmail || '');
	const emptyContact = !currentEmail || !currentPhone;
	const emptyInfos = isDefault
		? emptyContact || !bankData || currentEmailIsInvalid
		: emptyContact || currentEmailIsInvalid;

	const showContactAndBankEmptyInfoModal =
		isDefault && (emptyContact || currentEmailIsInvalid) && !bankData;
	const showBankEmptyInfoModal = isDefault && !bankData;
	const showInvalidEmailEmptyInfoModal = !emptyContact && currentEmailIsInvalid;

	const handleToggleModal = (modal: Modals) => {
		setOpenModals(prev => ({...prev, [modal]: !prev[modal]}));
	};

	const handleChangeAccordionForm = (name: FormStatesType) => {
		if (formChangeData.formActive === name) {
			resetFormChangeData();
			return;
		}

		if (formChangeData.formActive !== name && formChangeData.isFormDirty) {
			handleToggleModal('discardChanges');
			return;
		}

		setFormChangeData({
			isFormDirty: false,
			formActive: name,
		});
	};

	const handleConfirm = () => {
		const newFormName =
			formChangeData.formActive === FormStatesEnum.ACCOUNT
				? FormStatesEnum.BANK
				: FormStatesEnum.ACCOUNT;
		setFormChangeData({
			isFormDirty: false,
			formActive: newFormName,
		});
		handleToggleModal('discardChanges');
	};

	useEffect(() => {
		if (!loadingBankData) {
			if (showContactAndBankEmptyInfoModal) {
				setEmptyVariantState(EmptyInfoModalEnum.CONTACT_AND_BANK);
				return;
			}

			if (showBankEmptyInfoModal) {
				setEmptyVariantState(EmptyInfoModalEnum.BANK);
				return;
			}

			if (showInvalidEmailEmptyInfoModal) {
				setEmptyVariantState(EmptyInfoModalEnum.INVALID_EMAIL);
				return;
			}

			if (emptyContact) {
				setEmptyVariantState(EmptyInfoModalEnum.CONTACT);
			}
		}
	}, [loadingBankData, bankData, emptyContact, isDefault]);

	const shouldShowEmptyModal = !loadingBankData && !errorBankData;

	const getDataTagId = () => {
		if (isDefault) {
			return RefundTaggingEnum.CLICK_CONTINUAR_SOLICITACAO_REEMBOLSO;
		}
		if (isAdditionalDocuments) {
			return RefundTaggingEnum.CLICK_CONTINUAR_PARA_ENVIO_DE_DOCUMENTOS_COMPLEMENTARES;
		}
		if (isPreview) {
			return RefundTaggingEnum.CLICK_CONTINUAR_SOLICITAR_PREVIA_REEMBOLSO;
		}
		return undefined;
	};

	return (
		<Fragment>
			<Container>
				<StyledGrid.Root mt={isTablet ? 0 : 2} spacing={3}>
					<StyledGrid.Item xs={12} md={8} lg={8}>
						<Stack gap={2}>
							{isTablet ? <UserInfo /> : null}
							<ContactInfo
								user={{
									cellphone: currentPhone,
									email: currentEmail,
								}}
								dataTagId={RefundTaggingEnum.VISUALIZOU_EDICAO_DADOS_PERFIL}
							/>

							{isDefault ? (
								<Fragment>
									<BankAlert
										dataTagId={
											RefundTaggingEnum.CLICK_LER_MAIS_INFOS_SOLICITACAO
										}
										isRefundFlow
									/>
									<StyledBox>
										<Bank
											onEmptyAction={() =>
												handleChangeAccordionForm(FormStatesEnum.BANK)
											}
											handleChangeForm={() =>
												handleChangeAccordionForm(FormStatesEnum.BANK)
											}
											showForm={false}
											hiddenChangeAction
											dataTagId={
												RefundTaggingEnum.VISUALIZOU_EDICAO_DADOS_BANCARIOS
											}
											isExternalLoading={loadingValidateBank}
											bankAccountWarning={
												isRefundBankAndAgencyEnabled &&
												validateBankData?.isValid ===
													BankValidityEnum.INVALID
													? {
															title: invalidBankAlert.title,
															description:
																invalidBankAlert.description,
														}
													: undefined
											}
										/>
									</StyledBox>
								</Fragment>
							) : null}
						</Stack>

						<Stack
							direction="row"
							marginTop={2}
							justifyContent="flex-end"
							px={betweenMobileAndTablet ? 2 : 0}>
							<CTAButtonFixed.Root>
								<CTAButtonFixed.Action
									data-tag-id={getDataTagId()}
									size="large"
									onClick={handleNextStep}
									disabled={emptyInfos || loadingValidateBank}>
									{keep}
								</CTAButtonFixed.Action>
							</CTAButtonFixed.Root>
						</Stack>
					</StyledGrid.Item>

					{!isTablet ? (
						<StyledGrid.Item xs={12} md={4} lg={4}>
							<UserInfo hideAccordion />
						</StyledGrid.Item>
					) : null}
				</StyledGrid.Root>
			</Container>
			<StyledFooterContainer>
				<MainFooter />
			</StyledFooterContainer>

			<DiscardChangesModal
				open={openModals.discardChanges}
				setOpen={() => handleToggleModal('discardChanges')}
				onConfirm={handleConfirm}
			/>

			{shouldShowEmptyModal ? (
				<EmptyInfoModal open={emptyInfos} variant={emptyVariantState} />
			) : null}
		</Fragment>
	);
};
